import { type IStore, type IStorePackage } from '../stores/app/types';

export enum EnumKycStatus {
  init = 'INIT',
  pending = 'PENDING',
  completed = 'COMPLETED',
  requested = 'REQUESTED',
  notStarted = 'NOT_STARTED',
  expired = 'EXPIRED',
  failed = 'FAILED',
}

export interface IGetStorePackagesResponse {
  store: IStorePackage[];
}

export interface IGetStoreResponse {
  store: IStore;
  npu_store: IStore;
}

export interface IOpenPaymentRequest {
  packageId: number;
  paymentType: string;
}

export interface IOpenRedeemRequest {
  coins: number;
  paymentType: string;
}

export interface IUserDetailsData {
  firstName: string;
  lastName: string;
  dateOfBirth: string | Date;
  gender?: string;
  address: string;
  city: string;
  zipCode: string;
  territoryKey: string;
}

export interface IRedeemRequest {
  amount?: number;
  ipAddress?: string;
  paymentMethodType?: string;
  paymentType?: string;
  userPMId?: number;
  transactionId?: number;
}

export type ApiResponse<Data, Field extends string = 'data', E = string> = {
  data: { success: boolean } & Record<Field, Data>;
  errors: E[];
};

export type LDFlags =
  | 'ftue'
  | 'web_infinite_carousel'
  | 'liveops'
  | 'offers_feat'
  | 'gifts_feat'
  | 'messages_feat'
  | 'sound_effects'
  | 'nuvei_payment_credit_card'
  | 'sweep_coin_gameplay_in_development'
  | 'low_balance_offer'
  | 'enable_coins_animation'
  | 'welcome_offers'
  | 'web_inactivity_reload_minutes'
  | 'search_enabled'
  | 'should_check_force_kyc'
  | 'maintenance'
  | 'prevent_purchase'
  | 'invite_friends_promotion'
  | 'token_ex'
  | 'remove_credit_card_enabled'
  | 'prevent_multisession_enable'
  | 'mild_failures_design'
  | 'payments_separate_project'
  | 'loyalty_program'
  | 'feat_loyalty_program_tutorial'
  | 'skrill_in_payments_app'
  | 'open_ministore_on_low_balance'
  | 'tournaments'
  | 'daily_missions_feature'
  | 'handle_failures_within_payments_app'
  | 'nuvei_redeem_credit_card'
  | 'paysafe_redeem_skrill'
  | 'nuvei_redeem_bank_account'
  | 'paysafe_redeem_bank_account'
  | 'paysafe_payment_apple_pay'
  | 'paysafe_payment_credit_card'
  | 'paysafe_redeem_paysafe_card'
  | 'paysafe_3ds'
  | 'paysafe_payment_skrill'
  | 'nuvei_payment_bank_account'
  | 'payment_provider_fallback'
  | 'payment_provider_fallback_excluded'
  | 'web_edit_user_profile'
  | 'enable_client_logs'
  | 'update_balance_through_notification'
  | 'intercom_web_live_chat'
  | 'daily_bonus_feature'
  | 'purchase_event_debug'
  | 'enable_game_provider_logs'
  | 'trust_pilot_banner'
  | 'redemption_ibt_on_be'
  | 'terms_of_service_update'
  | 'daily_mission_title'
  | 'coin_back_feat_client'
  | 'coin_back_booster_feat'
  | 'coin_back_tutorial_feat'
  | 'bingo_ftue_tutorial_feat'
  | 'redeem_IBT_validate_details'
  | 'redemption_approval_notification'
  | 'block_signup_with_email'
  | 'user_free_spins_widgets_feature'
  | 'poc-enabled'
  | 'web_apple_pay_js'
  | 'cc_giveaway_on_low_balance'
  | 'web_profile_info'
  | 'custom_landing_feat'
  | 'direct_registration_landing_images'
  | 'card_payment_native_provider_variation'
  | 'fs_widget_add_player_frb'
  | 'game_labels_feat'
  | 'slot_machines_frb_widgets_enable'
  | 'client_side_event_start'
  | 'minimum_first_redeem_amount'
  | 'minimum_redeemable_amount'
  | 'minimum_cash_redeemable_amount'
  | 'daily_mission_grand_prize_enable'
  | 'payments_project_url'
  | 'web_allow_close_popups_shortcut'
  | 'quests_feat_client'
  | 'redemption-limits-enabled'
  | 'payments_restricted_region_purchase'
  | 'nuvei_webhook_on_payment_service'
  | 'wizard_based_kyc'
  | 'redeem_challenge'
  | 'payment_methods'
  | 'store_offers_feature_enable'
  | 'end_round_message_keys'
  | 'insufficient_funds_messages'
  | 'home_widgets_order'
  | 'snowflakes_background'
  | 'balance_animation_enabled'
  | 'metagames_enabled'
  | 'enable_previous_tournament_logic'
  | 'claim_hammer_animation_enable'
  | 'payouts_redeem_credit_card'
  | 'prize_drop'
  | 'hammers_enabled'
  | 'enable_offer_rive_animation'
  | 'flow_kill_switch'
  | 'flow_debug_switch'
  | 'enable_banner_rive_animation'
  | 'enable_old_search_mode'
  | 'amex_payments_enabled'
  | 'game_ui_web'
  | 'paysafe_redeem_interac'
  | 'prize_drop_animation_url'
  | 'marketing_consent'
  | 'enable_message_rive_animation'
  | 'enable_gift_rive_animation'
  | 'prize_drop_pre_message'
  | 'track_skrill_payment_session'
  | 'puzzle'
  | 'puzzle_pre_message'
  | 'puzzle_claim_animation_file_url'
  | 'prevent_popup_auto_display_web'
  | 'allow-redemption-upo-removal'
  | 'sign_up_flow_country_selection'
  | 'force_country_selection'
  | 'phone_verification_dialog'
  | 'ui_redesign_verification_flow'
  | 'promotions_terms_url'
  | 'purchase_phone_verification_enabled'
  | 'purchase_deposit_limits_preflight_enabled'
  | 'enable_barouletta_max_prize'
  | 'sc_mode_available'
  | 'redeem_mode_available'
  | 'responsible_gaming_dialog'
  | 'enable_features_dates'
  | 'enable_offer_disclaimer'
  | 'promo_codes_feature'
  | 'crown_sports_feature'
  | 'language_change_available'
  | 'banking_methods_supporting_generic_flow'
  | 'sepa_redeem'
  | 'subscription_feat_client'
  | 'trail_game'
  | 'interactive_animation_ftue_enabled'
  | 'rive_ftue_config'
  | 'ui_redesign_top_bar_account_icon'
  | 'ui_redesign_balance'
  | 'store_payment_methods'
  | 'ui_redesign_navigation_bar'
  | 'sc_mode_address_check_enabled'
  | 'gift_localization'
  | 'message_localization'
  | 'offer_localization'
  | 'new_location_block_pop_ups'
  | 'bingo_enabled'
  | 'ui_redesign_tooltip'
  | 'ui_redesign_dialogs'
  | 'road_feature_web'
  | 'new_rewards_animation'
  | 'plinko_claim_animation_file_url'
  | 'game_ui_new_authentication'
  | 'cms_disabled'
  | 'bingo_claim_animation_file_url'
  | 'ui_redesign_profile'
  | 'payment_router'
  | 'pay_com_apple_pay_retry'
  | 'pay_com_google_pay_retry'
  | 'ui_redesign_lounge'
  | 'bingo_animation_ftue_enabled'
  | 'bingo_ftue_config'
  | 'monetization_events_enabled'
  | 'challenges_feat'
  | 'ui_redesign_store'
  | 'ui_redesign_registration_flow'
  | 'registration_flow_rive_static_fallback'
  | 'enable_game_mode_selection'
  | 'roulette_claim_animation_file_url'
  | 'ui_redesign_monthly_bonus'
  | 'ui_redesign_promo_link'
  | 'rolling_offer_force_claim_animation_file_url'
  | 'landing_page_v2'
  | 'unified-limits-enabled'
  | 'event_trigger_actions'
  | 'profile_location_toggle'
  | 'extended_store_offer_enabled'
  | 'provider_search_enabled'
  | 'new_free_spins_icon_ui'
  | 'accessibility_enabled'
  | 'ui_redesign_daily_bonus'
  | 'payments_app_version'
  | 'sof-enabled'
  | 'invite_friends_enabled'
  | 'optimove_enabled'
  | 'store_package_label_new_design'
  | 'redeem_new_design'
  | 'ui_redesign_game_item'
  | 'share_sport_enable'
  | 'chase_bar_enabled'
  | 'chase_bar_config'
  | 'is_super_coin_territory'
  | 'numbered_games';

export type OAuthPlatform = 'facebook' | 'google' | 'apple';

export type SignInPlatform = 'email' | OAuthPlatform;

export type GeoData = {
  ip: string;
  country: string;
  countryCode: string;
  region: string;
  city: string;
  isVPN: boolean;
  ipVersion: 4 | 6;
  isGps?: boolean;
  geoVersion?: number;
};

interface RiveFileData {
  url: string;
  stateMachineName: string;
  skip_tutorial?: number;
}

export type RiveFTUEConfig = {
  mobile_m: RiveFileData;
  mobile_web: RiveFileData;
  tablet: RiveFileData;
  desktop: RiveFileData;
  disableGiftCards?: boolean;
} | null;

export interface AnimationGuideProgress {
  stepName: string;
  riveFileName: string;
  firebaseUserId: string;
  eventName: string;
  stageNum: number;
}

export type BingoFtueProgress = AnimationGuideProgress;
